// lint rule not applicable to V2 components

import { useState } from 'react';

import type { Contentful } from '@/contentful/contentful-exports';

import ActiveStatesGroup from './ActiveStatesGroup';
import HoveredLabel from './HoveredLabel';
import InactiveStatesGroup from './InactiveStatesGroup';
import useStyles from './InteractiveMap.styles';
import { StateType } from './State';
import { stateLabels, statePaths } from './stateInfo';
import {
  type InteractiveMapColors,
  type InteractiveMapStateColorScheme,
  interactiveMapColorsMap,
  solarAndBatteryColorSchemeDefault,
  solarOnlyColorSchemeDefault,
} from './styleConstants';

export function getStateColor(
  hoveredState: string | undefined,
  currentState: string,
  scheme: InteractiveMapStateColorScheme,
  disableHover: boolean
) {
  if (disableHover) return scheme.primaryColor;
  return currentState === hoveredState
    ? scheme.hoverColor
    : scheme.primaryColor;
}

export interface ActiveStates extends Contentful {
  fields: {
    state: string;
    stateInfoUrl?: string;
  };
}

export interface InteractiveMapContent extends Contentful {
  fields: {
    title?: string;
    solarOnlyColor?: InteractiveMapColors;
    solarAndBatteryColor?: InteractiveMapColors;
    solarAndBatteryStates: ActiveStates[] | undefined;
    solarOnlyStates: ActiveStates[] | undefined;
  };
}

interface Props {
  content: InteractiveMapContent;
}

export default function InteractiveMap({ content }: Props) {
  const { classes } = useStyles();
  const [hoveredState, setHoveredState] = useState<string | undefined>();
  const {
    title,
    solarOnlyColor,
    solarAndBatteryColor,
    solarAndBatteryStates = [],
    solarOnlyStates = [],
  } = content?.fields || {};
  const solarOnlyColorScheme: InteractiveMapStateColorScheme = solarOnlyColor
    ? interactiveMapColorsMap[solarOnlyColor]
    : solarOnlyColorSchemeDefault;
  const solarAndBatteryColorScheme = solarAndBatteryColor
    ? interactiveMapColorsMap[solarAndBatteryColor]
    : solarAndBatteryColorSchemeDefault;

  const allActive = new Set(
    [...solarAndBatteryStates, ...solarOnlyStates].map(
      (stateInfo) => stateInfo?.fields?.state
    )
  );

  const allInactive = Object.keys(statePaths).filter(
    (state) => !allActive.has(state)
  );

  if (hoveredState && !Object.keys(stateLabels).includes(hoveredState)) {
    console.warn('label for state not found, please add label');
  }

  return (
    <div className={classes.container}>
      {title && <h1 className={classes.heading}>{title}</h1>}
      {/** biome-ignore lint/a11y/noSvgWithoutTitle: Automated migration to Biome */}
      <svg
        version='1.1'
        xmlns='http://www.w3.org/2000/svg'
        className={classes.mainSvg}
        viewBox='0 0 940 600'
      >
        <desc style={{ WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)' }}>
          Created with Raphaël 2.1.0
        </desc>
        <defs style={{ WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)' }} />
        <ActiveStatesGroup
          states={solarAndBatteryStates}
          stateType={StateType.SOLAR_AND_BATTERY}
          hoveredState={hoveredState}
          setHoveredState={setHoveredState}
          colorScheme={solarAndBatteryColorScheme}
        />

        <ActiveStatesGroup
          states={solarOnlyStates}
          stateType={StateType.SOLAR_ONLY}
          hoveredState={hoveredState}
          setHoveredState={setHoveredState}
          colorScheme={solarOnlyColorScheme}
        />
        <InactiveStatesGroup states={allInactive} />
        {hoveredState && (
          <HoveredLabel
            stateLabel={stateLabels[hoveredState]}
            color={
              hoveredState === 'DC'
                ? solarOnlyColorScheme.hoverColor
                : solarAndBatteryColorScheme.hoverColor
            }
          />
        )}
      </svg>
      {/* <MapKey
        solarOnlyColor={
          solarOnlyColor
            ? interactiveMapColorsMap[solarOnlyColor].primaryColor
            : solarOnlyColorSchemeDefault.primaryColor
        }
        solarAndBatteryColor={
          solarAndBatteryColor
            ? interactiveMapColorsMap[solarAndBatteryColor].primaryColor
            : solarOnlyColorSchemeDefault.primaryColor
        }
      /> */}
    </div>
  );
}
